import api from '../../api';
import { denormalisedResponseEntities } from '../../util/data';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { types as sdkTypes } from '../../util/sdkLoader';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getSubscriptionDetailsData } from '../../util/userHelpers';
import { changeSubscription } from '../../ducks/paymentMethods.duck';

const { Money } = sdkTypes;

const MARK_TAB_UPDATED = 'app/ListingBulkUploadPage/MARK_TAB_UPDATED';
const CLEAR_UPDATED_TAB = 'app/ListingBulkUploadPage/CLEAR_UPDATED_TAB';

const FETCH_LISTING_BULK_ITEMS_REQUEST =
  'app/ListingBulkUploadPage/FETCH_LISTING_BULK_ITEMS_REQUEST';
const FETCH_LISTING_BULK_ITEMS_SUCCESS =
  'app/ListingBulkUploadPage/FETCH_LISTING_BULK_ITEMS_SUCCESS';
const FETCH_LISTING_BULK_ITEMS_ERROR = 'app/ListingBulkUploadPage/FETCH_LISTING_BULK_ITEMS_ERROR';

const UPDATE_LISTING_BULK_REQUEST = 'app/ListingBulkUploadPage/UPDATE_LISTING_BULK_REQUEST';
const UPDATE_LISTING_BULK_SUCCESS = 'app/ListingBulkUploadPage/UPDATE_LISTING_BULK_SUCCESS';
const UPDATE_LISTING_BULK_ERROR = 'app/ListingBulkUploadPage/UPDATE_LISTING_BULK_ERROR';

const DISCARD_LISTING_BULK_REQUEST = 'app/ListingBulkUploadPage/DISCARD_LISTING_BULK_REQUEST';
const DISCARD_LISTING_BULK_SUCCESS = 'app/ListingBulkUploadPage/DISCARD_LISTING_BULK_SUCCESS';
const DISCARD_LISTING_BULK_ERROR = 'app/ListingBulkUploadPage/DISCARD_LISTING_BULK_ERROR';

const CLEAR_WIZARD = 'app/ListingBulkUploadPage/CLEAR_WIZARD';

// ================ Reducer ================ //

const initialState = {
  updatedTab: null,
  updateInProgress: false,
  updateBulkError: null,

  discardInProgress: false,
  discardBulkError: null,

  fetchInProgress: false,
  fetchError: null,
  listingBulk: null,

  listingBulkItems: [],
  fetchBulkItemsInProgress: false,
  fetchBulksItemsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CLEAR_WIZARD:
      return { ...initialState };

    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateBulkError: null };

    case FETCH_LISTING_BULK_ITEMS_REQUEST:
      return { ...state, fetchBulkItemsInProgress: true, fetchBulksItemsError: null };
    case FETCH_LISTING_BULK_ITEMS_SUCCESS: {
      const previousItems = state.listingBulkItems;
      return {
        ...state,
        fetchBulkItemsInProgress: false,
        listingBulkItems: payload.items?.length > 0 ? payload.items : previousItems,
        listingBulkItemsPagination: payload.pagination,
      };
    }
    case FETCH_LISTING_BULK_ITEMS_ERROR:
      return { ...state, fetchBulkItemsInProgress: false, fetchBulksItemsError: payload };

    case UPDATE_LISTING_BULK_REQUEST:
      return { ...state, updateInProgress: true, updateBulkError: null };
    case UPDATE_LISTING_BULK_SUCCESS:
      return {
        ...state,
        updateInProgress: false,
        listingBulk: payload,
        listingBulkItems: payload.uploadedListings,
      };
    case UPDATE_LISTING_BULK_ERROR:
      return { ...state, updateInProgress: false, updateBulkError: payload };

    case DISCARD_LISTING_BULK_REQUEST:
      return { ...state, discardInProgress: true, discardBulkError: null };
    case DISCARD_LISTING_BULK_SUCCESS:
      return { ...state, discardInProgress: false };
    case DISCARD_LISTING_BULK_ERROR:
      return { ...state, discardInProgress: false, discardBulkError: payload };

    default:
      return state;
  }
}

const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

const clearWizard = () => ({
  type: CLEAR_WIZARD,
});

const fetchListingBulkItemsRequest = () => ({ type: FETCH_LISTING_BULK_ITEMS_REQUEST });
const fetchListingBulkItemsSuccess = (items, pagination) => ({
  type: FETCH_LISTING_BULK_ITEMS_SUCCESS,
  payload: { items, pagination },
});
const fetchListingBulkItemsError = error => ({
  type: FETCH_LISTING_BULK_ITEMS_ERROR,
  payload: error,
});

const updateListingBulkRequest = () => ({ type: UPDATE_LISTING_BULK_REQUEST });
const updateListingBulkSuccess = payload => ({ type: UPDATE_LISTING_BULK_SUCCESS, payload });
const updateListingBulkError = error => ({ type: UPDATE_LISTING_BULK_ERROR, payload: error });

const discardListingBulkRequest = () => ({ type: DISCARD_LISTING_BULK_REQUEST });
const discardListingBulkSuccess = () => ({ type: DISCARD_LISTING_BULK_SUCCESS });
const discardListingBulkError = error => ({ type: DISCARD_LISTING_BULK_ERROR, payload: error });

export const fetchListingBulk = (id, config, search) => {
  return (dispatch, getState, sdk) => {
    dispatch(fetchListingBulkItemsRequest());
    const queryParams = parse(search);

    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = 'listing-card',
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;

    const filterByBulkId = id && id !== '0' ? { pub_bulkId: id } : {};
    const params = {
      include: ['images', 'currentStock'],
      ...filterByBulkId,
      sort: 'pub_hasBulkError,createdAt',
      page: queryParams?.page || 1,
      perPage: 18,
      'fields.image': [
        // Scaled variants for large images
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',

        // Cropped variants for listing thumbnail images
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    };

    return api.listingBulks
      .getListingBulk(id)
      .then(res =>
        dispatch(updateListingBulkSuccess({ listingBulk: res.data, uploadedListings: [] }))
      )
      .then(() => sdk.ownListings.query(params))
      .then(res => {
        const { meta: pagination } = res?.data || {};
        const data = denormalisedResponseEntities(res);
        dispatch(fetchListingBulkItemsSuccess(data, pagination));
      })
      .catch(e => {
        dispatch(fetchListingBulkItemsError(e));
      });
  };
};

export const createListingBulk = (tab, body) => async (dispatch, getState) => {
  dispatch(updateListingBulkRequest());

  return api.listingBulks
    .createListingBulk(body)
    .then(res => {
      const { listingBulk, uploadedListings } = res.data || {};

      const formattedListings = uploadedListings.map(listing => ({
        ...listing,
        attributes: {
          ...listing.attributes,
          price: new Money(listing.attributes.price.amount, listing.attributes.price.currency),
        },
      }));

      dispatch(updateListingBulkSuccess({ listingBulk, uploadedListings: formattedListings }));
      dispatch(markTabUpdated(tab));

      return res;
    })
    .catch(e => {
      dispatch(updateListingBulkError(e));
      throw new Error(e);
    });
};

export const publishListingBulk = (tab, bulkId) => async dispatch => {
  dispatch(updateListingBulkRequest());

  return api.listingBulks
    .publishListingBulk(bulkId)
    .then(res => {
      dispatch(updateListingBulkSuccess(res?.data));

      dispatch(markTabUpdated(tab));

      return res;
    })
    .catch(e => {
      dispatch(updateListingBulkError(e));
    });
};

export const discardListingBulk = (tab, bulkId) => async dispatch => {
  dispatch(discardListingBulkRequest());

  return api.listingBulks
    .discardListingBulk(bulkId)
    .then(res => {
      dispatch(discardListingBulkSuccess());

      dispatch(markTabUpdated(tab));

      return res;
    })
    .catch(e => {
      dispatch(discardListingBulkError(e));
    });
};

// loadData is run for each tab of the wizard
export const loadData = (params, search, config) => (dispatch, getState) => {
  dispatch(clearUpdatedTab());
  dispatch(fetchCurrentUser());

  const { subscription } = getState().user.currentUser?.attributes?.profile?.metadata || {};
  const { limitExceeded } = getSubscriptionDetailsData(subscription);

  const { id } = params;

  const { listingBulk } = getState().ListingBulkUploadPage;

  if (id !== listingBulk?._id) {
    dispatch(clearWizard());
  }

  if (!id || id === '0') {
    return Promise.resolve();
  } else if (limitExceeded) {
    dispatch(changeSubscription());
  }

  return dispatch(fetchListingBulk(id, config, search));
};
